export interface Project {
  id: string;
  name: string;
  description?: string;
  start_date?: string;
  finish_date?: string;
  end_date?: string;
  status?: 'active' | 'archived';
  company_id?: number;
  progress?: number;
  percent_complete?: number;
  is_critical?: boolean;
  created_at?: string;
  updated_at?: string;
  total_activities?: number;
  project_metadata?: any;
  schedules?: Schedule[];
}

export type ScheduleStatus = 'pending' | 'processing' | 'completed' | 'error' | 'archived';

export interface Schedule {
  id: string;
  project_id: string;
  name: string;
  version?: string;
  uploaded_at?: string;
  file_path?: string;
  status?: ScheduleStatus;
  parsed_data?: any;
}

export interface Activity {
  id: string;
  name: string;
  code: string;
  early_start_date: string;
  early_end_date: string;
  late_start_date: string;
  late_end_date: string;
  target_start_date: string;
  target_end_date: string;
  actual_start_date: string;
  actual_end_date: string;
  total_float_hr_cnt: number;
  is_critical: boolean;
  phys_complete_pct: number;
}

export interface Resource {
  id: number;
  project_id: number;
  resource_id: string;
  name: string;
  type: string;
  units: number;
  rate: number;
  cost: number;
}

export interface Relationship {
  id: number;
  project_id: number;
  pred_activity_id: string;
  succ_activity_id: string;
  relationship_type: string;
  lag: number;
}

// Enhanced Activity interface with all date fields
export interface ScheduleActivity {
  id: string;
  name: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  expected_finish_date?: string;
  duration?: number;
  percent_complete?: number;
  percent_complete_type?: string;
  physical_percent_complete?: number;
  duration_percent_complete?: number;
  units_percent_complete?: number;
  complete_pct_type?: string; // CP_Phys, CP_Drtn, CP_Units
  phys_complete_pct?: number; // Raw from XER (0-1 scale)
  effective_percent_complete?: number; // Primary display value (unified model)
  task_status_inferred?: string; // Inferred status (same as status for safety)
  warnings?: string[]; // List of warnings for data inconsistencies
  status?: string;
  wbs_id?: string;
  parent_wbs_id?: string;
  wbs_level?: number;
  original_id?: string;
  
  // Additional date fields
  early_start_date?: string;
  early_end_date?: string;
  late_end_date?: string;
  target_start_date?: string;
  target_finish_date?: string;
  planned_start_date?: string;
  planned_finish_date?: string;
  actual_start_date?: string;
  actual_finish_date?: string;
  
  // Additional fields
  type?: string;
  calendar_id?: string;
  calendar_name?: string;
  activity_id?: string;
  total_float?: number;
  free_float?: number;
  notes?: string;
  priority?: string;
  critical?: boolean;
  duration_type?: string;
  original_duration?: number;
  remaining_duration?: number;
  primary_constraint_date?: string;
  primary_constraint_type?: string;
  secondary_constraint_date?: string;
  secondary_constraint_type?: string;
  constraints_data?: any;
  activity_metadata?: any;
  wbs_name?: string;
  
  // User Defined Fields (UDF) - dynamically detected, no limit
  [key: string]: any; // Allow any additional UDF fields
}

// Column configuration for customizable table
export interface ActivityColumn {
  key: keyof ScheduleActivity;
  label: string;
  description: string;
  category: 'basic' | 'dates' | 'progress' | 'advanced' | 'udf';
  defaultVisible: boolean;
  sortable: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

// Predefined column configurations
export const ACTIVITY_COLUMNS: ActivityColumn[] = [
  // Basic columns
  {
    key: 'id',
    label: 'Activity ID',
    description: 'Activity ID (task code) from the schedule',
    category: 'basic',
    defaultVisible: true,
    sortable: true,
    width: '120px'
  },
  {
    key: 'name',
    label: 'Name',
    description: 'Activity name/description',
    category: 'basic',
    defaultVisible: true,
    sortable: true,
    width: '220px'
  },
  {
    key: 'type',
    label: 'Type',
    description: 'Activity type (Task, Milestone, etc.)',
    category: 'basic',
    defaultVisible: false,
    sortable: true,
    width: '100px'
  },
  {
    key: 'status',
    label: 'Status',
    description: 'Current activity status',
    category: 'basic',
    defaultVisible: true,
    sortable: true,
    width: '120px'
  },
  
  // Date columns
  {
    key: 'start_date',
    label: 'Start Date',
    description: 'General start date used in P6 calculations',
    category: 'dates',
    defaultVisible: true,
    sortable: true,
    width: '120px'
  },
  {
    key: 'end_date',
    label: 'End Date',
    description: 'General end date used in P6 calculations',
    category: 'dates',
    defaultVisible: true,
    sortable: true,
    width: '120px'
  },
  {
    key: 'early_start_date',
    label: 'Early Start',
    description: 'Earliest possible start date from CPM calculation',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'early_end_date',
    label: 'Early End',
    description: 'Soonest the activity could finish',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'late_end_date',
    label: 'Late End',
    description: 'Latest date without delaying the project',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'planned_start_date',
    label: 'Planned Start',
    description: 'Start date from baseline/original plan',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'planned_finish_date',
    label: 'Planned Finish',
    description: 'Originally planned finish date',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'target_start_date',
    label: 'Target Start',
    description: 'User-defined baseline start date',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'target_finish_date',
    label: 'Target Finish',
    description: 'User-defined baseline finish date',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'actual_start_date',
    label: 'Actual Start',
    description: 'Date the activity actually started',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'actual_finish_date',
    label: 'Actual Finish',
    description: 'Date the activity actually finished',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'expected_finish_date',
    label: 'Expected Finish',
    description: 'Expected/late finish date',
    category: 'dates',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  
  // Progress columns
  {
    key: 'duration',
    label: 'Duration (hrs)',
    description: 'Activity duration in hours (from schedule)',
    category: 'progress',
    defaultVisible: true,
    sortable: true,
    width: '120px'
  },
  {
    key: 'percent_complete',
    label: '% Complete',
    description: 'Percentage of work completed',
    category: 'progress',
    defaultVisible: true,
    sortable: true,
    width: '100px'
  },
  {
    key: 'original_duration',
    label: 'Original Duration',
    description: 'Original planned duration',
    category: 'progress',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  {
    key: 'remaining_duration',
    label: 'Remaining Duration',
    description: 'Remaining work duration',
    category: 'progress',
    defaultVisible: false,
    sortable: true,
    width: '120px'
  },
  
  // Advanced columns
  {
    key: 'total_float',
    label: 'Total Float',
    description: 'Total float from XER (display in d or hr)',
    category: 'advanced',
    defaultVisible: false,
    sortable: true,
    width: '100px'
  },
  {
    key: 'free_float',
    label: 'Free Float',
    description: 'Free float from XER (display in d or hr)',
    category: 'advanced',
    defaultVisible: false,
    sortable: true,
    width: '100px'
  },
  {
    key: 'critical',
    label: 'Critical',
    description: 'Whether activity is on critical path',
    category: 'advanced',
    defaultVisible: false,
    sortable: true,
    width: '80px'
  },
  {
    key: 'priority',
    label: 'Priority',
    description: 'Activity priority level',
    category: 'advanced',
    defaultVisible: false,
    sortable: true,
    width: '80px'
  },
  {
    key: 'wbs_name',
    label: 'WBS',
    description: 'Work Breakdown Structure name',
    category: 'advanced',
    defaultVisible: true,
    sortable: true,
    width: '360px'
  },
  {
    key: 'calendar_name',
    label: 'Calendar',
    description: 'Assigned calendar name',
    category: 'advanced',
    defaultVisible: false,
    sortable: true,
    width: '150px'
  },
  {
    key: 'description',
    label: 'Description',
    description: 'Detailed activity description',
    category: 'advanced',
    defaultVisible: false,
    sortable: false
  },
  {
    key: 'notes',
    label: 'Notes',
    description: 'Activity notes',
    category: 'advanced',
    defaultVisible: false,
    sortable: false
  }
] 